document.addEventListener('DOMContentLoaded', () => {
  const OPEN_DELAY_MS = 50;    // theme: add "visible" after ~50ms
  const CLOSE_DELAY_MS = 350;  // theme: remove "active" after ~350ms

  // -------------------------------
  // Core Drawer Functions
  // -------------------------------

  function closeAllDrawers() {
    document.querySelectorAll('.drawer.visible, .drawer.active').forEach((el) => {
      restoreColorsSwatch(el);
      el.classList.remove('visible');
      el.querySelectorAll('.visible').forEach((child) => child.classList.remove('visible'));
      setTimeout(() => el.classList.remove('active'), CLOSE_DELAY_MS);
    });
    document.body.classList.remove('drawer-is-open');
  }

  function openDrawer(drawer) {
    if (!drawer) return;
    closeAllDrawers();

    drawer.classList.add('active');
    document.body.classList.add('drawer-is-open');

    setTimeout(() => {
      drawer.classList.add('visible');
    }, OPEN_DELAY_MS);
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    restoreColorsSwatch(drawer);
    drawer.classList.remove('visible');
    drawer.querySelectorAll('.visible').forEach((el) => el.classList.remove('visible'));
    setTimeout(() => drawer.classList.remove('active'), CLOSE_DELAY_MS);
    document.body.classList.remove('drawer-is-open');
  }

  function mountColorsSwatch(drawer) {
    const productSection = drawer.closest('[data-section-type="product"]');
    const drawerContent = drawer.querySelector('.drawer__content');
    const sourceSwatch = productSection?.querySelector('variant-king-combined-listing');

    if (!sourceSwatch || !drawerContent) return;

    if (!drawer._sourceSwatchParent) {
      drawer._sourceSwatchParent = sourceSwatch.parentNode;
      drawer._sourceSwatchNextSibling = sourceSwatch.nextSibling;
    }

    if (drawerContent.contains(sourceSwatch)) return;

    drawerContent.innerHTML = '';

    const swatchWrapper = document.createElement('div');
    swatchWrapper.className = 'product__swatch';

    swatchWrapper.appendChild(sourceSwatch);
    drawerContent.appendChild(swatchWrapper);

    drawer._sourceSwatchMounted = true;
  }

  function restoreColorsSwatch(drawer) {
    if (!drawer?._sourceSwatchMounted) return;

    const sourceSwatch = drawer.querySelector('variant-king-combined-listing');
    const originalParent = drawer._sourceSwatchParent;
    const originalNextSibling = drawer._sourceSwatchNextSibling;
    const drawerContent = drawer.querySelector('.drawer__content');

    if (sourceSwatch && originalParent) {
      if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
        originalParent.insertBefore(sourceSwatch, originalNextSibling);
      } else {
        originalParent.appendChild(sourceSwatch);
      }
    }

    if (drawerContent) {
      drawerContent.innerHTML = '';
    }

    drawer._sourceSwatchMounted = false;
  }

  // -------------------------------
  // Drawer Setup
  // -------------------------------

  const drawers = document.querySelectorAll('.drawer[data-drawer]');
  drawers.forEach((drawer) => {
    const key = drawer.dataset.drawer;

    // Open triggers
    document.querySelectorAll(`[data-drawer="${key}"]`).forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        // ---------------------------------
        // Special case: COLORS drawer
        // ---------------------------------
        if (key === 'colors') {
          mountColorsSwatch(drawer);
        }

        openDrawer(drawer);
      });
    });

    // Close triggers (overlay or close button)
    const overlay = drawer.querySelector('[data-overlay]');
    const closeBtn = drawer.querySelector('[data-drawer-close]');
    [overlay, closeBtn].forEach((el) => {
      el?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeDrawer(drawer);
      });
    });

    if (key === 'colors') {
      const drawerContent = drawer.querySelector('.drawer__content');

      drawerContent?.addEventListener('click', (ev) => {
        const swatch = ev.target.closest('[swatch-url]');
        if (!swatch || !drawerContent.contains(swatch)) return;

        ev.preventDefault();

        const url = swatch.getAttribute('swatch-url');
        if (url) {
          window.location.href = url;
        }
      });
    }

    // Prevent drawer container clicks from closing
    const container = drawer.querySelector('.drawer__container');
    if (container) {
      container.addEventListener('click', (e) => {
        // If the click is *directly* on the close button, allow normal close
        const isCloseButton = e.target.closest('[data-drawer-close]');
        if (isCloseButton) return; // Let closeBtn handler run

        // Otherwise, prevent bubbling (avoids overlay handling)
        e.stopPropagation();
      });
    }
  });

  // Rehydrate Shopify sections after Variant King swaps page DOM.
  // Variant King replaces `main#main`, which drops Flickity/zoom state.
  const sectionApi = window.Shopify?.theme?.sections;
  let rehydrateQueued = false;

  function cleanupDetachedSectionInstances() {
    if (!sectionApi?.instances?.length) return;

    sectionApi.instances.slice().forEach((instance) => {
      if (instance?.container?.isConnected) return;

      try {
        instance.onUnload?.();
      } catch (error) {
        console.warn('[Theme] Section unload failed during rehydrate:', error);
      }

      const index = sectionApi.instances.indexOf(instance);
      if (index > -1) {
        sectionApi.instances.splice(index, 1);
      }
    });
  }

  function initSections(root) {
    if (!sectionApi?.registered || !sectionApi?.instances || !root) return;

    const sectionNodes = [];
    if (root.nodeType === 1 && root.matches('[data-section-type]')) {
      sectionNodes.push(root);
    }
    root.querySelectorAll?.('[data-section-type]').forEach((el) => sectionNodes.push(el));

    sectionNodes.forEach((container) => {
      const type = container.getAttribute('data-section-type');
      const Section = sectionApi.registered[type];
      if (!Section) return;

      const alreadyMounted = sectionApi.instances.some((instance) => instance.container === container);
      if (alreadyMounted) return;

      try {
        new Section(container);
      } catch (error) {
        console.warn('[Theme] Section reinit failed:', type, error);
      }
    });
  }

  function rehydrateSections(root) {
    if (rehydrateQueued) return;
    rehydrateQueued = true;

    requestAnimationFrame(() => {
      rehydrateQueued = false;
      cleanupDetachedSectionInstances();
      initSections(root || document);
      window.dispatchEvent(new Event('resize'));
    });
  }

  const sectionSwapObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;

        if (node.matches?.('main#main')) {
          rehydrateSections(node);
          return;
        }

        const main = node.querySelector?.('main#main');
        if (main) {
          rehydrateSections(main);
          return;
        }
      }
    }
  });

  if (document.body) {
    sectionSwapObserver.observe(document.body, { childList: true, subtree: true });
  }

  // ESC to close any drawer
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllDrawers();
  });
});
